import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DialogData, ResponseType } from './edition-selector.types';
import { AppPrice } from '@galaxy/marketplace-apps';

interface State {
  editions: Edition[];
  window: Window;
  currency: string;
  appPrice: AppPrice;
}

interface Edition {
  editionId: string;
  name: string;
}

interface Window {
  leftIndex: number;
  rightIndex: number;
}

export const windowSize = 3;
export const tabletScreenMaxSize = 840; //SASS: $media--tablet-large-minimum
@Component({
  templateUrl: 'edition-selector.component.html',
  styleUrls: ['edition-selector.component.scss'],
  standalone: false,
})
export class EditionSelectorComponent {
  state$: Observable<State>;
  window$$ = new BehaviorSubject<Window>({ leftIndex: 0, rightIndex: windowSize - 1 });
  editionsCount = 0;
  carouselItemCountDesktop = 3;
  carouselItemCountPhone = 1;
  windowSize = windowSize;

  constructor(
    private readonly dialogRef: MatDialogRef<EditionSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: DialogData,
  ) {
    this.state$ = combineLatest([this.data.currency$, this.window$$, this.data.appPrice$]).pipe(
      map(([currency, window, appPrice]) => {
        const allEditions: Edition[] = [];
        this.data.editionsMarketing.forEach((e) => {
          allEditions.push({
            editionId: e.editionId,
            name: e.name,
          });
        });
        this.editionsCount = allEditions.length;
        return {
          editions: allEditions,
          window: window,
          currency: currency,
          appPrice: appPrice,
        };
      }),
    );
  }

  shiftCarousel(windowIndices: Window, direction: number): void {
    let scrollTo: number;
    let block: ScrollLogicalPosition;
    if (
      window.innerWidth <= tabletScreenMaxSize &&
      windowIndices.rightIndex - windowIndices.leftIndex > this.carouselItemCountPhone
    ) {
      windowIndices.rightIndex = windowIndices.leftIndex + this.carouselItemCountPhone - 1;
    } else if (
      window.innerWidth > tabletScreenMaxSize &&
      windowIndices.rightIndex - windowIndices.leftIndex < this.carouselItemCountDesktop - 1
    ) {
      let newRightIndex = windowIndices.leftIndex + this.carouselItemCountDesktop - 1;
      if (newRightIndex >= this.editionsCount) {
        windowIndices.leftIndex = Math.max(windowIndices.leftIndex - newRightIndex + windowIndices.rightIndex, 0);
        newRightIndex = this.editionsCount - 1;
      }
      windowIndices.rightIndex = newRightIndex;
    }
    const leftIndex = windowIndices.leftIndex + direction;
    const rightIndex = windowIndices.rightIndex + direction;
    if (direction < 0) {
      scrollTo = leftIndex;
      block = 'start';
    } else if (direction > 0) {
      scrollTo = rightIndex;
      block = 'end';
    }
    if (leftIndex < 0 || rightIndex > this.data.editionsMarketing.length - 1) {
      return;
    }
    document.getElementById(`carousel-col-${scrollTo}`).scrollIntoView({
      behavior: 'smooth',
      block: block,
    });
    this.window$$.next({ leftIndex: leftIndex, rightIndex: rightIndex });
  }

  selectEdition(editionId: string): void {
    this.dialogRef.close({
      productId: this.data.productId,
      editionId: editionId,
      type: ResponseType.EditionSelected,
    });
  }

  compareEditions(): void {
    this.dialogRef.close({
      type: ResponseType.CompareEditions,
    });
  }
}
